<script lang="ts" setup>
    import { onMounted, watch, reactive, computed, ref} from 'vue';
    import { PageTitle } from '../../LibMain';
    import WindowHeaderFooter from '../../components/WindowHeaderFooter.vue';
    import * as ReportModel from '../../lib/Models/Reports/FinancialReportModel';
    import ActionButtonV2 from '../../components/ActionButtonV2.vue'; 
    import { ActionButtonStyle } from '../../lib/Types/Types'; 
    import { popAlert } from '../../lib/Notification';
    import { DatabaseIdTypes } from '../../Enums/ChoiceEnums';
    import { ButtonDisplaySize, DateDisplayFormat } from "../../Enums/UiEnums";
    import { ICol, RenderModeType } from "../../lib/ChoiceTableHelpers";
    import ChoiceTable from "../../components/ChoiceTable.vue";
    import Icon from '../../components/Icon.vue';
    import { patientSetupScreenUrl, claimEntryScreenUrl, claimCommentScreenUrl, officeCommunicationUrl, patientCommentScreenUrl } from '../../lib/OfficeMdUrls';
    import { NoTzDate, toDisplayDateString, toDate } from '../../lib/DateUtils';
    import DropDownSplitButton from '../../components/DropDownSplitButton.vue';
    import DropDownButtonItem from '../../components/DropDownButtonItem.vue';
    import * as ChoiceApi from '../../lib/ChoiceApi';

    import '../../../css/choice.css';

    const props = defineProps<{
        screenName: string,
        monthlyReportStartDate: NoTzDate,
        monthlyReportEndDate: NoTzDate,
        selectedGroupBy1: string,
        selectedGroupBy2?: string,
        selectedDateType: ReportModel.FinancialReportDateTypes,
        detailRowItem: any
    }>();

    const monthlyFinancialDrillDownTable = ref<InstanceType<typeof ChoiceTable>>();        

    const tableColumns = ref<ICol[]>([
        { Name: 'Actions', DataField: 'empty' },
        { Name: 'Claim Id', DataField: 'claimId' },
        { Name: 'Claim Status', DataField: 'claimStatus' }, 
        { Name: 'Claim DOS', DataField: 'dateOfService', RenderMode: RenderModeType.PrettyDate },
        { Name: 'Claim Submission Date', DataField: 'lastClaimSubmissionDate', RenderMode: RenderModeType.PrettyDate },
        { Name: 'Chart', DataField: 'patientId' },
        { Name: 'Patient Name', DataField: 'patientName' },
        { Name: 'Primary Payer', DataField: 'primaryPayerName' },
        { Name: 'Secondary Payer', DataField: 'secondaryPayerName' },
        { Name: 'Tertiary Payer', DataField: 'tertiaryPayerName' },
        { Name: 'Charges', DataField: 'chargeAmount', RenderMode: RenderModeType.AccountingMoney },
        { Name: 'Patient Payments', DataField: 'patientPaymentTotal', RenderMode: RenderModeType.AccountingMoney },
        { Name: 'Insurance Payments', DataField: 'insurancePaymentTotal', RenderMode: RenderModeType.AccountingMoney },
        { Name: 'Total Payments', DataField: 'paymentTotal', RenderMode: RenderModeType.AccountingMoney },
        { Name: 'Total Adjustments', DataField: 'adjustmentTotal', RenderMode: RenderModeType.AccountingMoney },
        { Name: 'Balance', DataField: 'claimBalance', RenderMode: RenderModeType.AccountingMoney },  
        { Name: 'Deposit ID', DataField: 'depositId' },
        { Name: 'Receipt ID', DataField: 'receiptId' }        
    ]);
    
    const reportData = reactive<{   
        rowDetail: any[],
        drillDownInputs: ReportModel.IMonthlyDrillDownInputParameters,
        drillDownData?: ReportModel.IFinancialReportResults[],
        groupBy1Title?: string,
        groupBy2Title?: string
        }>({  
            rowDetail: [] ,
            drillDownInputs:
            {
                searchDateStart: toDate(props.monthlyReportStartDate)!,
                searchDateEnd: toDate(props.monthlyReportEndDate)!,
                searchDateType: props.selectedDateType,
                group1Column: props.selectedGroupBy1,
                group1Value: props.detailRowItem.group1,
                group2Column: props.selectedGroupBy2,
                group2Value: props.detailRowItem.group2
            },
            groupBy1Title: `${props.selectedGroupBy1} = ${props.detailRowItem.group1}`,
            groupBy2Title: `${props.selectedGroupBy2} = ${props.detailRowItem.group2}`
    });

    onMounted(async () => {         
        showDetailReport();
    });

    watch(() => props.detailRowItem, (_newVal, _oldVal) => showDetailReport());  
    
    const emit = defineEmits<{
        (e: "onShowDrillDown", showDrillDown: boolean) : void
    }>();

    async function showDetailReport() {
        emit("onShowDrillDown", true);

        //we need to query the server for drill down data, too large to pass down entire month.  And we will not show total detail, too large.
        const resp = await ChoiceApi.reporting.monthlyFinancials.getMonthlyFinancialReportDrillDownData(reportData.drillDownInputs);

        if (!resp) {
            await popAlert("There was an error pulling the drill down data on the Monthly Financial Report", "Monthly Financial Report Drill Down");
            return;
        }

        reportData.drillDownData = resp;

        //reportData.rowDetail = `total` === itemClicked ? props.drillDownData : matchingItems;   
    }

     async function exportDrillDownReport() {
        try {
            const downloadData = reportData.drillDownData?.map(item => ({
                    "Claim Id": item.claimId,
                    "Claim Status": item.claimStatus,
                    "Chart": item.patientId,
                    "Patient Name": item.patientName,
                    "Claim DOS": toDisplayDateString(item.dateOfService, DateDisplayFormat.NumericDate),
                    "Charge Post Date": toDisplayDateString(item.chargePostDate, DateDisplayFormat.NumericDate),
                    "Last Claim Submission Date": toDisplayDateString(item.lastClaimSubmissionDate, DateDisplayFormat.NumericDate),
                    "Claim Creator": item.claimCreatorId,
                    "Rendering Provider": item.renderingProviderName,
                    "Service Location": item.serviceLocationName,
                    "Primary Payer Financial Class": item.primaryPayerFinancialClassDesc,
                    "Primary Payer": item.primaryPayerName,
                    "Secondary Payer": item.secondaryPayerName,
                    "Tertiary Payer": item.tertiaryPayerName,
                    "CPT": item.cptCode,
                    "CPT Description": item.cptDesc,
                    "Deposit Id": item.depositId,
                    "Receipt Id": item.receiptId,
                    "Payment Post Date": toDisplayDateString(item.paymentPostDate, DateDisplayFormat.NumericDate),
                    "Payment Method": item.paymentMethod,
                    "Payment User": item.paymentUserId,
                    "Charges": item.chargeAmount,
                    "Patient Payments": item.patientPaymentTotal,
                    "Insurance Payments": item.insurancePaymentTotal,
                    "Total Payments": item.paymentTotal,
                    "Total Adjustments": item.adjustmentTotal,
                    "Unapplied Amount": item.unappliedAmount,
                    "Refund Total": item.refundTotal,
                    "Claim Balance": item.claimBalance
                }));

            const wb = XLSX.utils.book_new()
            const ws1 = XLSX.utils.json_to_sheet(downloadData);
            XLSX.utils.book_append_sheet(wb, ws1, "MonthlyDrillDownReportData");
            XLSX.writeFile(wb, "MonthlyFinancialReportDrillDownData.xls");
        }
        catch (e) {
            await popAlert("Error downloading monthly data to Excel: " + e);
        }	
    }

    async function getMainReport() : Promise<void> {
        emit("onShowDrillDown", false);
    } 

    function openClaim(row: any) {
        window.open(claimEntryScreenUrl(row.claimId), "_blank", "resizable=true,width=1050,height=900");
    }

    function openPatient(row: any) {
        window.open(patientSetupScreenUrl(DatabaseIdTypes.patient_id, row.patientId ?? 0), "_blank", "resizable=true,width=1050,height=900");
    }

    function openClaimComment(row: any) {
        window.open(claimCommentScreenUrl(row.claimId), "_blank", "resizable=true,width=1050,height=700");
    }

    async function openPatientComment(row: any) {
        //get patient entity id using the patient id
        const entityData = await ChoiceApi.entityInfo.getEntityInfoByDatabaseId({ id: row.patientId, idType: DatabaseIdTypes.patient_id });
        window.open(patientCommentScreenUrl(entityData.entityId), "_blank", "resizable=true,width=1050,height=700");
    }

    function openOfficeCommunication(row: any, includeClaim: boolean) {
        if (includeClaim) {
            window.open(officeCommunicationUrl(row.patientId, row.claimId), "_blank", "resizable=true,width=900,height=600");
        }  
        else {
            window.open(officeCommunicationUrl(row.patientId), "_blank", "resizable=true,width=900,height=600");
        }      
    }

    async function printDrillDownReport() {
        try{
            document.title = 'Monthly Financial Drill Down Report.pdf';
            window.print();
        }
        catch(e){
            console.log("Error printing drill down report: " + e);
        }
    }
    
</script>

<style>
    @media print {
        .no-print { display: none; }
        body { width: 1500px; } 
}
</style>


<template>
    <PageTitle :security-screen-name="props.screenName" >
        <template #permitted>   
            <WindowHeaderFooter :headerHeight="3.0">
                <template v-slot:WindowHeader>
                    <div class="row mt-2">
                        <div class="col col-md-6 isalus-page-title-large pt-1">Monthly Financial Report Drill Down</div>
                    </div>
                </template>
                <template v-slot:WindowBody>  
                    <!--DRILL DOWN DETAIL-->
                    <div v-if="reportData.drillDownData && reportData.drillDownData.length < 1" class="text-center fs-6"> 
                        No records found matching your report criteria<br>
                        <ActionButtonV2 
                            :action="ActionButtonStyle.Backward"
                            :text="'Back to Main Report'"
                            :size="ButtonDisplaySize.Smaller" 
                            :performAction="getMainReport"
                        />
                    </div>
                    <div v-if="reportData.drillDownData && reportData.drillDownData.length > 0">
                        <div class="row">
                            <div class="col label-isalus-medium">
                                Group By: {{ props.detailRowItem == "total" ? "Total" : reportData.groupBy1Title }} 
                            </div>
                            <div class="col label-isalus-medium" v-if="props.selectedGroupBy2">
                                SubGroup By: {{ reportData.groupBy2Title }}
                            </div>
                            <div class="col-md-6 text-end no-print mb-2">
                                <ActionButtonV2 
                                    :action="ActionButtonStyle.Backward"
                                    :text="'Back to Main Report'"
                                    :size="ButtonDisplaySize.Smaller" 
                                    :performAction="getMainReport"
                                    :endingPadding="2"
                                />
                                <ActionButtonV2 
                                    :action="ActionButtonStyle.Print"
                                    :text="'Print'"
                                    :size="ButtonDisplaySize.Smaller" 
                                    :spinText="'Printing...'"
                                    :performAction="printDrillDownReport"
                                    :endingPadding="1"
                                />
                                <ActionButtonV2 
                                    :action="ActionButtonStyle.Export"
                                    :text="'Export to Excel'"
                                    :size="ButtonDisplaySize.Smaller" 
                                    :spinText="'Exporting...'"
                                    :performAction="exportDrillDownReport" 
                                />
                            </div>
                        </div>                  

                        <div class="row pt-1"> 
                            <ChoiceTable 
                                ref="monthlyFinancialDrillDownTable"
                                :columns="tableColumns" 
                                :data="reportData.drillDownData" 
                                :show-filters="false" 
                                :allowSorting="true"
                                fontSize="smaller"
                                :itemsPerPage="10"                
                                :showPaginationButtonsBelowChoiceTable="true"
                                :key-value-data-member="'claimId'"
                            >
                                <template #cell="{col, row, value}">
                                    <template v-if="'Actions' === col.Name" class="no-print">
                                        <DropDownSplitButton :baseClass="'btn btn-primary label-isalus-small'">
                                            <template v-slot:button><i class="fa-solid fa-gear"></i></template>
                                            <DropDownButtonItem v-if="row.claimId" :baseClass="'dropdown-item label-isalus-small'" @click="openClaim(row as any)">
                                                Open the claim
                                            </DropDownButtonItem>
                                            <DropDownButtonItem v-if="row.claimId" :baseClass="'dropdown-item label-isalus-small'" @click="openClaimComment(row as any)">
                                                Open the claim comment
                                            </DropDownButtonItem>
                                            <DropDownButtonItem v-if="row.claimId" :baseClass="'dropdown-item label-isalus-small'" @click="openOfficeCommunication(row as any, true)">
                                                Open the claim communication
                                            </DropDownButtonItem>
                                            <DropDownButtonItem :baseClass="'dropdown-item label-isalus-small'" @click="openPatient(row as any)">
                                                Open the patient
                                            </DropDownButtonItem>
                                            <DropDownButtonItem :baseClass="'dropdown-item label-isalus-small'" @click="openPatientComment(row as any)">
                                                Open the patient comment
                                            </DropDownButtonItem>
                                            <DropDownButtonItem :baseClass="'dropdown-item label-isalus-small'" @click="openOfficeCommunication(row as any, false)">
                                                Open the patient communication
                                            </DropDownButtonItem>
                                        </DropDownSplitButton>
                                    </template>
                                    <template v-if="'Claim Id' === col.Name">
                                        <span style="cursor: pointer;" @click="openClaim(row as any)" title="Open the claim">
                                            <u>{{ row.claimId }}</u>
                                        </span>                                        
                                    </template>
                                    <template v-if="'Chart' === col.Name">
                                        <span style="cursor: pointer;" @click="openPatient(row as any)" title="Open the patient">
                                            <u>{{ row.patientId }}</u>
                                        </span>                                        
                                    </template>                                
                                </template>
                            </ChoiceTable>                  
                        </div>  
                    </div>                          
                </template>
            </WindowHeaderFooter> 
        </template>
    </PageTitle>
</template>